import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Modal } from '@getro/rombo';

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Intro = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.main};
`;

const ImplicationsList = styled.ul`
  margin: 0;
  padding-left: 32px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.main};
`;

const Implication = styled.li`
  & + & {
    margin-top: 8px;
  }
`;

const MfaRequiredConfirmModal = ({ action, isSubmitting, onCancel, onConfirm }) => {
  const isEnable = action === 'enable';

  return (
    <Modal
      title={isEnable ? 'Require two-factor authentication?' : 'Stop requiring two-factor authentication?'}
      isOpen
      onCancel={onCancel}
      actions={
        <Actions>
          <Button onClick={onCancel} variant="tertiary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant={isEnable ? 'primary' : 'danger'}
            loading={isSubmitting}
            onClick={(event) => {
              event.stopPropagation();
              onConfirm();
            }}
            data-cy="mfa-required-confirm"
          >
            {isEnable ? 'Enable' : 'Disable'}
          </Button>
        </Actions>
      }
      data-testid={isEnable ? 'mfa-required-enable-modal' : 'mfa-required-disable-modal'}
    >
      {isEnable ? (
        <>
          <Intro>Requiring two-factor authentication will:</Intro>
          <ImplicationsList>
            <Implication>
              Require all team members to set up two-factor authentication before they can log in
            </Implication>
            <Implication>
              Require those members to complete two-factor setup on their next login before using Getro
            </Implication>
            <Implication>
              ⚠️ Sign those members out of any active sessions, including the browser extension, within 15 minutes
            </Implication>
            <Implication>Not affect team members who already use two-factor authentication</Implication>
          </ImplicationsList>
        </>
      ) : (
        <>
          <Intro>Stopping this requirement will:</Intro>
          <ImplicationsList>
            <Implication>
              Allow team members to log in without setting up two-factor authentication for this network
            </Implication>
            <Implication>
              Keep existing two-factor setups on individual accounts. Members can continue using them optionally.
            </Implication>
            <Implication>Not sign anyone out or change their personal security settings</Implication>
          </ImplicationsList>
        </>
      )}
    </Modal>
  );
};

MfaRequiredConfirmModal.propTypes = {
  action: PropTypes.oneOf(['enable', 'disable']).isRequired,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

MfaRequiredConfirmModal.defaultProps = {
  isSubmitting: false,
};

export default MfaRequiredConfirmModal;
